import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { History, ArrowDownLeft, ArrowUpRight, Loader2, Activity, Calendar, Clock, Ticket as TicketIcon, X, Copy, MessageCircle } from 'lucide-react';
import { Transaction, User } from '../types';
import { getTransactions } from '../services/storage';
import { Skeleton } from './Skeleton';
import { supabase } from '../supabaseClient';

interface WalletHistoryProps {
  currentUser: User;
}

const TicketModal = ({ tx, onClose }: { tx: Transaction; onClose: () => void }) => {
  const [timeLeft, setTimeLeft] = useState(20 * 60);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const rawItemName = tx.item_name || 'Item';
  const cleanItemName = rawItemName.replace(/^Purchased\s+/i, '');
  
  // Logic: Show code if available, otherwise show Processing...
  const displayCode = tx.verification_code ? tx.verification_code : 'Processing...';
  const isPending = tx.status === 'pending';

  const waLink = `https://wa.me/60148543483?text=${encodeURIComponent(
    `Hi Puchi! I just bought ${cleanItemName}. \n\nTransaction ID: ${tx.id}\nCode: ${displayCode}`
  )}`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="glass-panel w-full max-w-sm rounded-3xl border border-accent-500/30 overflow-hidden relative shadow-neon"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={20}/></button>
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-accent-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-accent-500/30 shadow-neon">
             <TicketIcon className="text-accent-500" size={32} />
          </div>
          <h3 className="text-xl font-bold text-white mb-1">Order Ticket</h3>
          <p className="text-zinc-500 text-xs font-mono mb-8">{tx.item_name}</p>

          <div className="bg-black/60 border border-white/10 rounded-2xl p-6 mb-6 relative overflow-hidden group">
             <p className="text-[9px] text-zinc-500 uppercase font-bold tracking-widest mb-2">Verification Hash</p>
             <div className="flex items-center justify-center gap-2 mb-4">
                 <span className={`text-2xl font-mono font-bold tracking-widest ${displayCode === 'Processing...' ? 'text-zinc-500 text-sm animate-pulse' : 'text-white'}`}>
                   {displayCode}
                 </span>
                 {displayCode !== 'Processing...' && (
                    <button onClick={() => navigator.clipboard.writeText(displayCode)} className="text-zinc-600 hover:text-white"><Copy size={14} /></button>
                 )}
             </div>
             <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono ${isPending ? 'bg-orange-900/20 text-orange-400' : 'bg-green-900/20 text-green-400'}`}>
                <Clock size={12} />
                <span>Status: {isPending ? 'Validating...' : 'Confirmed'}</span>
             </div>
          </div>

          <a 
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-500 text-white font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95 uppercase tracking-wider text-xs"
          >
             <MessageCircle size={18} /> Verify on WhatsApp
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
};

const WalletHistory: React.FC<WalletHistoryProps> = ({ currentUser }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null);

  useEffect(() => {
    const fetchHistory = async () => {
      setIsLoading(true);
      try {
        const data = await getTransactions(currentUser.id);
        setTransactions(Array.isArray(data) ? data : []);
      } catch (e) {
        setTransactions([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchHistory();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.id) return;
    const channel = supabase.channel(`realtime:history_tx:${currentUser.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'transactions', filter: `user_id=eq.${currentUser.id}` }, 
        (payload) => {
           if (payload.eventType === 'INSERT') {
               const raw = payload.new as any;
               const formattedTx: Transaction = {
                  ...raw,
                  description: raw.description || raw.item_name || 'Transaction',
                  item_name: raw.item_name || raw.description || 'Transaction',
                  status: raw.status || 'completed',
                  verification_code: raw.verification_code,
                  amount: raw.amount ?? 0
               };
               setTransactions(prev => [formattedTx, ...prev]);
           } else if (payload.eventType === 'UPDATE') {
               const updated = payload.new as any;
               // Update list
               setTransactions(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated, amount: updated.amount ?? t.amount } : t));
               
               // Update modal if open - Realtime refresh for the user
               if (selectedTx && selectedTx.id === updated.id) {
                  setSelectedTx(prev => prev ? { 
                      ...prev, 
                      ...updated, 
                      verification_code: updated.verification_code || prev.verification_code 
                  } : null);
               }
           }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); }
  }, [currentUser.id, selectedTx]);

  return (
    <div className="max-w-4xl mx-auto pb-10 animate-fade-in">
      <div className="flex items-center gap-4 mb-8">
        <div>
           <h2 className="text-2xl font-bold text-white tracking-tight">Logbook</h2>
           <p className="text-zinc-500 text-xs font-mono mt-1">BLOCKCHAIN IMMUTABLE RECORD</p>
        </div>
      </div>

      <div className="glass-panel rounded-3xl border border-white/5 overflow-hidden">
        {isLoading ? (
          <div className="divide-y divide-white/5">
             {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="p-4 flex justify-between">
                   <Skeleton className="h-4 w-20 bg-zinc-800" />
                   <Skeleton className="h-4 w-40 bg-zinc-800" />
                </div>
             ))}
          </div>
        ) : (transactions?.length ?? 0) === 0 ? (
          <div className="p-20 text-center text-zinc-600 font-mono">
             <History size={48} className="mx-auto mb-4 opacity-30" />
             <p>NO DATA FOUND.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
              {transactions.map((t, index) => {
                const isNegative = ['purchase','deduction','transfer_out'].includes(t.type);
                const dateObj = new Date(t.created_at);
                const description = t.description || t.item_name || 'Transaction';
                const isPending = t.status === 'pending';

                return (
                  <motion.div
                    key={t.id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors group"
                  >
                    <div className="flex items-center gap-4 overflow-hidden">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border border-white/5 shrink-0 ${isNegative ? 'bg-zinc-900 text-zinc-400' : 'bg-green-500/10 text-green-500'}`}>
                             {isPending ? <Clock size={16} className="animate-pulse text-orange-500" /> : isNegative ? <ArrowUpRight size={16}/> : <ArrowDownLeft size={16}/>}
                        </div>
                        <div className="min-w-0">
                             <p className="text-sm font-bold text-white truncate">{description}</p>
                             <div className="flex items-center gap-2 mt-0.5">
                                 <span className="text-[10px] text-zinc-500 font-mono">{dateObj.toLocaleDateString()}</span>
                                 <span className={`text-[9px] px-1.5 py-0.5 rounded border uppercase ${
                                     isPending ? 'border-orange-500/30 text-orange-500' : 'border-white/10 text-zinc-600'
                                 }`}>
                                     {isPending ? 'Pending' : t.type.replace('_',' ')}
                                 </span>
                             </div>
                        </div>
                    </div>

                    <div className="text-right shrink-0 ml-4">
                        {isPending || t.type === 'purchase' ? (
                            <button 
                                onClick={() => setSelectedTx(t)}
                                className="bg-accent-500 hover:bg-white text-black font-bold text-[10px] px-3 py-1.5 rounded-lg uppercase transition-all"
                            >
                                Ticket
                            </button>
                        ) : (
                            <span className={`font-mono font-bold text-sm ${isNegative ? 'text-zinc-400' : 'text-green-500'}`}>
                                {isNegative ? '-' : '+'}${t.amount.toFixed(2)}
                            </span>
                        )}
                    </div>
                  </motion.div>
                );
              })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedTx && <TicketModal tx={selectedTx} onClose={() => setSelectedTx(null)} />}
      </AnimatePresence>
    </div>
  );
};

export default WalletHistory;